import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCooperative from 'core/hooks/useCooperative';
import { Card, Col, Row } from 'antd';
import { motion } from 'framer-motion';

// components
import BackButton from 'components/backButton/backButton';
import PhoneInputComponent from 'components/phoneInput/phoneInput';
import FilePreview from 'components/filePreview/filePreview';
import SharesCount from 'components/sharesCount/sharesCount';
import PageTitle from 'components/title/title';
import DisabledElement from 'components/disabledElement/disabledElement';
import LoadingComponent from 'components/loadingComponent/loadingComponent';

// services
import NotificationService from 'core/services/notification';
import { onGetShareholderInfoById } from '../services/shareholderInfoServices';
import { isRTL } from 'core/services/language';
import { dateFormat } from 'utils/dateFormat';
import { formatFileSize } from 'utils/FileSize';

// models
import { ShareholderInfo } from '../models/shareholderInfo';
import { useNavigate, useParams } from 'react-router-dom';

interface ShareholderInfoViewPageProps {}

const ShareholderInfoViewPage: FunctionComponent<ShareholderInfoViewPageProps> = () => {
	const { t } = useTranslation();
	const { isCooperative } = useCooperative();

	const navigate = useNavigate();
	const { national_id } = useParams<{ national_id: string }>();

	const [isLoading, setIsLoading] = useState(true);
	const [shareholderInfo, setShareholderInfo] = useState<ShareholderInfo>();

	useEffect(() => {
		getShareholderInfo();
	}, []);

	const getShareholderInfo = async () => {
		try {
			if (national_id === undefined) {
				NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
				navigate('/dashboard/actions/shareholder-info');
			}

			setIsLoading(true);

			const shareholderInfoResponse = await onGetShareholderInfoById(national_id as string);
			setShareholderInfo(shareholderInfoResponse);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/shareholder-info');
			setIsLoading(false);
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton styles={{ paddingTop: 16 }} />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<motion.div
					initial={{ opacity: 0, transform: 'translateY(-10px)' }}
					animate={{ opacity: 1, transform: 'translateY(0)' }}
					transition={{ duration: 0.3 }}>
					<Card>
						<h5>{t('ACTIONS.SHAREHOLDER_INFO')}</h5>

						<Row gutter={[16, 16]}>
							<Col span={24} lg={12}>
								<DisabledElement
									fullWidth
									label={t('ACTIONS.SHAREHOLDER_FULL_NAME')}
									value={isRTL() ? shareholderInfo?.shareholder.name : shareholderInfo?.shareholder.name_en ?? shareholderInfo?.shareholder.name}
								/>
							</Col>
							<Col span={24} lg={12}>
								<DisabledElement fullWidth label={t('COMMON.NATIONAL_ID')} value={shareholderInfo?.shareholder.national_id} />
							</Col>

							<Col span={24} lg={12}>
								<DisabledElement
									fullWidth
									label={t('COMMON.DATE_OF_BIRTH')}
									value={dateFormat(shareholderInfo?.shareholder.date_of_birth! , true)}
								/>
							</Col>

							<Col span={24} lg={12}>
								<PhoneInputComponent
									disabled
									fullWidth
									userDialCode={shareholderInfo?.shareholder.phone_country_code}
									userPhoneNumber={shareholderInfo?.shareholder.phone_number}
								/>
							</Col>

							<Col span={24} lg={12}>
								<SharesCount
									fullWidth
									title={t('COMMON.COMMON_SHARES')}
									total={shareholderInfo?.common_shares_count || 0}
									free={shareholderInfo?.free_common_shares_count || 0}
									suspended={shareholderInfo?.suspended_common_shares_count || 0}
									mortgaged={shareholderInfo?.mortgaged_common_shares_count || 0}
								/>
							</Col>

							{!isCooperative && (
							<Col span={24} lg={12}>
								<SharesCount
									fullWidth
									title={t('COMMON.PREFERRED_SHARES')}
									total={shareholderInfo?.preferred_shares_count || 0}
									free={shareholderInfo?.free_preferred_shares_count || 0}
									suspended={shareholderInfo?.suspended_preferred_shares_count || 0}
									mortgaged={shareholderInfo?.mortgaged_preferred_shares_count || 0}
								/>
							</Col>
							)}

							<Col span={24} className="mt-3">
								<FilePreview
									name={t('ACTIONS.SHAREHOLDER_CERTIFICATE')}
									data={formatFileSize(shareholderInfo?.certificate_url.size_in_kilobyte as number)}
									downloadLink={shareholderInfo?.certificate_url.download_link}
								/>
							</Col>
						</Row>
					</Card>
				</motion.div>
			)}
		</>
	);
};

export default ShareholderInfoViewPage;
